import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Skeleton, Tooltip } from '../../ui';
import { formatDate, formatNumber } from '../../../utils/formatting';
import { unitYear } from '../../../utils/inventoryView';
import SOURCE_LABELS from '../../../config/sourceLabels';
import {
    bounceText, bounceTone, completenessTone, ctrStatus, formatSession, last30, leadTone, monthlyBuckets, personLabel, sessionTone,
} from '../../../utils/unitOverview';
import { Sparkline } from '../charts';
import PairedBarChart from './barChart';
import { ExternalIcon } from '../icons';
import { tabCtxShape } from './tabContext';
import './OverviewTabV2.css';
import { AGING_TEXT_TONES_BY_ID, unitAgingBucketId, unitAgingLabel } from '../../../utils/dealershipSettings';
import { useDealershipSettings } from '../../../contexts/settingsContext';
import { dealHeadline } from '../../../utils/committedUnit';

// Aging bucket id → text tone (green, blue-ish, orange, red as today).
export const AGING_TEXT_TONES = AGING_TEXT_TONES_BY_ID;
const TONE_CLASS = { good: 'u2-pos', ok: 'u2-warn', warn: 'u2-warn', amber: 'u2-warn', poor: 'u2-neg' };

function Row({ label, children }) {
    return <div><dt>{label}</dt><dd>{children}</dd></div>;
}
Row.propTypes = { label: PropTypes.node, children: PropTypes.node };

function Metric({ label, value, valueClass, spark, sparkTone = 'sage', children, tip }) {
    return (
        <div className="u2-tile u2ov-metric">
            <span className="u2-tile-l">{label}{tip}</span>
            <span className={['u2-tile-n', valueClass].filter(Boolean).join(' ')}>{value}</span>
            {spark && spark.length > 1 && <Sparkline values={spark} tone={sparkTone} />}
            {children}
        </div>
    );
}
Metric.propTypes = {
    label: PropTypes.node, value: PropTypes.node, valueClass: PropTypes.string, spark: PropTypes.array,
    sparkTone: PropTypes.string, children: PropTypes.node, tip: PropTypes.node,
};

function CardSkeleton({ title }) {
    return (
        <section className="u2-card" aria-busy="true">
            <div className="u2-c-head">{title}</div>
            <Skeleton lines={3} />
        </section>
    );
}
CardSkeleton.propTypes = { title: PropTypes.node };

function ExtLink({ href, children }) {
    return (
        <a className="u2-linkb u2ov-ext" href={href} target="_blank" rel="noopener noreferrer">
            {children}<ExternalIcon />
        </a>
    );
}
ExtLink.propTypes = { href: PropTypes.string, children: PropTypes.node };

/** Marketplace performance, 30 days (P-UNIT-070); only for a listed unit. */
function MarketplaceCard({ unit, th, loading }) {
    const title = (
        <>
            <h3>{SOURCE_LABELS.marketplace} performance</h3>
            <span className="u2-sub">30 days</span>
        </>
    );
    const monthly = useMemo(() => monthlyBuckets(th?.daily_history, ['impressions', 'details_views', 'email_leads']), [th]);
    if (loading && !th) return <CardSkeleton title={title} />;
    if (!th) return null;
    const m30 = th.metrics_30d || {};
    const impressions = m30.impressions ?? 0;
    const views = m30.details_views ?? 0;
    const leads = m30.email_leads ?? 0;
    const ctr = ctrStatus(impressions, views);
    const lead = leadTone(leads, unit.DaysInStock);
    const active = th.status === 'ACTIVE';
    return (
        <section className="u2-card" aria-label={`${SOURCE_LABELS.marketplace} performance`}>
            <div className="u2-c-head">
                {title}
                <span className="u2-right">
                    <span className={`u2ov-badge ${active ? 'is-ok' : 'is-crit'}`}>{active ? 'Active' : 'Deleted'}</span>
                    {th.is_featured && <span className="u2ov-badge is-feat">Featured</span>}
                </span>
            </div>
            <div className="u2-tiles3">
                <Metric label="Impressions" value={formatNumber(impressions)} spark={last30(th.daily_history, 'impressions')}>
                    {impressions === 0 && <span className="u2-tile-s">No recent activity.</span>}
                </Metric>
                <Metric label="Details views" value={formatNumber(views)} spark={last30(th.daily_history, 'details_views')}>
                    {ctr.value && <span className={`u2ov-ctr is-${ctr.tone}`}>{ctr.value}% CTR — {ctr.label}</span>}
                </Metric>
                <Metric
                    label="Email leads"
                    value={leads}
                    valueClass={lead ? TONE_CLASS[lead] : undefined}
                    spark={last30(th.daily_history, 'email_leads')}
                    sparkTone="amber"
                />
            </div>
            <span className="u2-eyebrow">Contact &amp; save actions (30 days)</span>
            <div className="u2-tiles3">
                <Metric label="Calls" value={formatNumber(m30.clicks_to_call ?? 0)} />
                <Metric label="Texts" value={formatNumber(m30.clicks_to_text ?? 0)} />
                <Metric label="Saved" value={formatNumber(m30.total_watchlists ?? 0)} />
            </div>
            {monthly && monthly.monthCount >= 2 && (
                <>
                    <span className="u2-eyebrow">Monthly trend — last {monthly.monthCount} months</span>
                    <PairedBarChart
                        label={`${SOURCE_LABELS.marketplace} impressions and details views by month`}
                        labels={monthly.labels}
                        a={{ name: 'Impressions', values: monthly.series.impressions, tone: 'sagelight' }}
                        b={{ name: 'Details Views', values: monthly.series.details_views, tone: 'teal' }}
                    />
                </>
            )}
            {th.listing_url && <ExtLink href={th.listing_url}>View listing</ExtLink>}
        </section>
    );
}
MarketplaceCard.propTypes = { unit: PropTypes.object.isRequired, th: PropTypes.object, loading: PropTypes.bool };

/** Website engagement, 30 days (P-UNIT-071); only with a website page. */
function WebsiteCard({ ga, loading }) {
    const title = (
        <>
            <h3>{SOURCE_LABELS.website} engagement</h3>
            <span className="u2-sub">30 days</span>
        </>
    );
    const monthly = useMemo(() => monthlyBuckets(ga?.daily_history, ['pageviews', 'users']), [ga]);
    if (loading && !ga) return <CardSkeleton title={title} />;
    if (!ga) return null;
    const m30 = ga.metrics_30d || {};
    const pageviews = m30.pageviews ?? 0;
    const users = m30.users ?? 0;
    const session = m30.avg_session_sec ?? 0;
    const bounce = m30.bounce_rate ?? 0;
    const sTone = sessionTone(session);
    return (
        <section className="u2-card" aria-label={`${SOURCE_LABELS.website} engagement`}>
            <div className="u2-c-head">{title}</div>
            <div className="u2ov-tiles4">
                <Metric
                    label="Pageviews"
                    value={formatNumber(pageviews)}
                    spark={last30(ga.daily_history, 'pageviews')}
                    sparkTone="teal"
                    tip={<Tooltip label="What pageviews are" content="Times the product page was loaded" />}
                />
                <Metric
                    label="Unique users"
                    value={formatNumber(users)}
                    spark={last30(ga.daily_history, 'users')}
                    sparkTone="teal"
                    tip={<Tooltip label="What unique users are" content="Distinct visitors (approx.)" />}
                />
                <Metric
                    label="Avg session"
                    value={formatSession(session)}
                    valueClass={sTone ? TONE_CLASS[sTone] : undefined}
                    spark={last30(ga.daily_history, 'avg_session_sec')}
                    sparkTone="teal"
                />
                <Metric
                    label="Bounce rate"
                    value={bounceText(bounce, session)}
                    valueClass={TONE_CLASS[bounceTone(bounce)]}
                    spark={last30(ga.daily_history, 'bounce_rate')}
                    sparkTone="teal"
                />
            </div>
            {monthly && monthly.monthCount >= 2 && (
                <>
                    <span className="u2-eyebrow">Monthly trend — last {monthly.monthCount} months</span>
                    <PairedBarChart
                        label={`${SOURCE_LABELS.website} pageviews and unique users by month`}
                        labels={monthly.labels}
                        a={{ name: 'Pageviews', values: monthly.series.pageviews, tone: 'teal' }}
                        b={{ name: 'Unique Users', values: monthly.series.users, tone: 'amber' }}
                    />
                </>
            )}
            {ga.listing_url && <ExtLink href={ga.listing_url}>View on website</ExtLink>}
        </section>
    );
}
WebsiteCard.propTypes = { ga: PropTypes.object, loading: PropTypes.bool };

/**
 * Overview (P-UNIT-061…071) in the prototype's three-column cards. Today's
 * fields and fallbacks; the listing details sit in Equipment details. The
 * marketplace and website detail come from the page's loads (no new fetch).
 */
export default function OverviewTabV2({ ctx }) {
    const { unit, detail, committed } = ctx;
    const headline = dealHeadline(unit);
    const deal = committed ? headline : null;
    // An on-hold unit in stock: its hold (and reason) next to the stock status.
    const hold = !committed && headline?.status === 'on_hold' ? headline : null;
    const notReceived = committed && unit.DaysInStock == null;
    const { mfData, mfLoading } = detail;
    const { aging } = useDealershipSettings();
    const ageTone = AGING_TEXT_TONES[unitAgingBucketId(unit, aging)] || 'quiet';
    const ageLabel = unitAgingLabel(unit, aging);
    const quality = mfData ? completenessTone(mfData.completeness_pct) : null;
    return (
        <>
            <div className="u2-grid3">
                <section className="u2-card" aria-labelledby="u2ov-eq">
                    <div className="u2-c-head"><h3 id="u2ov-eq">Equipment details</h3></div>
                    <dl className="u2-dl">
                        <Row label="Make">{unit.MAKE}</Row>
                        <Row label="Model">{unit.MODEL}</Row>
                        <Row label="Category">{unit.CATEGORY}</Row>
                        <Row label="Year">{unitYear(unit) ?? '—'} (Manuf: {unitYear({ Model_Year: unit.YEAR_MANUF }) ?? '—'})</Row>
                        <Row label="Condition">{unit.NewUsedStatus}</Row>
                        {committed ? (
                            <Row label="Deal status">
                                <span className="u2ov-badge is-warn">{deal ? deal.text : 'Committed'}</span>
                            </Row>
                        ) : (
                            <Row label="Stock status">
                                {unit.StockStatus === 'P' && <span className="u2ov-badge is-warn">Pending</span>}
                                {unit.StockStatus === 'S' && <span className="u2ov-badge is-ok">In Stock</span>}
                                {!unit.StockStatus && '—'}
                            </Row>
                        )}
                        {hold && (
                            <Row label="Deal status">
                                <span className="u2ov-badge is-hold">{hold.label}</span>
                                {hold.reason ? ` · ${hold.reason}` : ' · Reason not recorded'}
                            </Row>
                        )}
                        <Row label="Branch">{unit.BranchName ?? unit.LOCATION ?? '—'}</Row>
                        {mfData?.serial_number && <Row label="Serial number"><span className="u2ov-mono">{mfData.serial_number}</span></Row>}
                        {mfData?.operation_hours != null && <Row label="Operation hours (listing)">{formatNumber(mfData.operation_hours)}</Row>}
                        {mfData?.horse_power != null && <Row label="Horsepower">{mfData.horse_power} HP</Row>}
                        {mfData?.status_key && <Row label="Listing status">{mfData.status_key}</Row>}
                        {mfData?.is_new != null && <Row label="Listing condition">{mfData.is_new ? 'New' : 'Used'}</Row>}
                    </dl>
                </section>

                <section className="u2-card" aria-labelledby="u2ov-age">
                    <div className="u2-c-head"><h3 id="u2ov-age">Aging &amp; inventory</h3></div>
                    <dl className="u2-dl">
                        <Row label="Date received">{notReceived ? '—' : formatDate(unit.DATEREC)}</Row>
                        {notReceived ? (
                            // A unit not yet received has no days in stock and no aging bucket.
                            <Row label="Days in stock">Not yet received</Row>
                        ) : (
                            <>
                                <Row label="Days in stock"><span className={`u2-${ageTone}`}>{unit.DaysInStock} days</span></Row>
                                <Row label="Aging bucket">
                                    {ageLabel
                                        ? <span className={`u2ov-badge is-${ageTone}`}>{ageLabel}</span>
                                        : '—'}
                                </Row>
                            </>
                        )}
                    </dl>
                </section>

                <section className="u2-card" aria-labelledby="u2ov-acq">
                    <div className="u2-c-head"><h3 id="u2ov-acq">Acquisition &amp; assignments</h3></div>
                    <dl className="u2-dl">
                        <Row label="Salesperson">{unit.Salesman || 'Unassigned'}</Row>
                        <Row label="Customer">{unit.Customer || 'None'}</Row>
                        <Row label="Acquired from">{personLabel(unit.AcquiredBy_FullName)}</Row>
                        <Row label="Previous driver">{personLabel(unit.PrevDriver_FullName)}</Row>
                    </dl>
                </section>

                {mfLoading && (
                    <section className="u2-card"><p className="u2-note">Loading {SOURCE_LABELS.listingLower} data...</p></section>
                )}

                {mfData && (
                    <section className="u2-card" aria-labelledby="u2ov-q">
                        <div className="u2-c-head">
                            <h3 id="u2ov-q">Listing quality</h3>
                            <span className={`u2-right u2ov-badge is-${quality}`}>{mfData.completeness_score}/6</span>
                        </div>
                        <div className="u2ov-qbar" aria-hidden="true">
                            <span className={`is-${quality}`} style={{ width: `${mfData.completeness_pct}%` }} />
                        </div>
                        <p className={`u2-note u2ov-q-${quality}`}>{mfData.completeness_score}/6 fields complete</p>
                        {mfData.missing_fields?.length > 0 && (
                            <p className="u2-note">
                                <b>Missing:</b>{' '}
                                {mfData.missing_fields.map((f) => <span key={f} className="u2ov-tag">{f}</span>)}
                            </p>
                        )}
                        {mfData.description && (
                            <>
                                <div className="u2-c-head"><h4>Listing description</h4></div>
                                <p className="u2-note u2ov-desc">{mfData.description}</p>
                            </>
                        )}
                    </section>
                )}

                {mfData?.options?.length > 0 && (
                    <section className="u2-card" aria-labelledby="u2ov-opt">
                        <div className="u2-c-head"><h3 id="u2ov-opt">Options &amp; attachments</h3><span className="u2-sub">from the listing</span></div>
                        <div className="u2ov-opts">
                            {mfData.options.map((opt, i) => <span key={`${opt}-${i}`}>{opt}</span>)}
                        </div>
                    </section>
                )}
            </div>

            {(unit.THStatus || unit.GAPagePath) && (
                <div className="u2-grid2 u2-grid2--even">
                    {unit.THStatus && <MarketplaceCard unit={unit} th={detail.thDetail} loading={detail.thLoading} />}
                    {unit.GAPagePath && <WebsiteCard ga={detail.ga4Detail} loading={detail.ga4Loading} />}
                </div>
            )}
        </>
    );
}

OverviewTabV2.propTypes = { ctx: tabCtxShape.isRequired };
